import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface FavouriteListing {
  id: string;
  listing_id: string;
  created_at: string;
  listing: {
    id: string;
    title: string;
    suburb: string;
    state: string;
    price_per_month: number;
    images: string[];
    space_type: string;
  } | null;
}

export default function FavouritesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favourites, setFavourites] = useState<FavouriteListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavourites = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('favourites')
          .select(`
            id,
            listing_id,
            created_at,
            listing:listing_id (
              id, title, suburb, state, price_per_month, images, space_type
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setFavourites(data || []);
      } catch (_error: unknown) {
        // silently handle error
      } finally {
        setLoading(false);
      }
    };
    fetchFavourites();
  }, [user?.id]);

  const removeFavourite = async (favouriteId: string) => {
    await supabase.from('favourites').delete().eq('id', favouriteId);
    setFavourites(prev => prev.filter(f => f.id !== favouriteId));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-96 pt-32">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-[#6366F1] animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading favourites...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">My Favourites</h1>
            <p className="text-gray-600 mt-2">Spaces you have saved</p>
          </div>

          {favourites.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favourites.map((fav) => (
                <div key={fav.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    <img src={fav.listing?.images?.[0] || ''} alt={fav.listing?.title || 'Space'} className="w-full h-48 object-cover cursor-pointer" onClick={() => navigate(`/listing/${fav.listing_id}`)} />
                    <button onClick={() => removeFavourite(fav.id)} className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-red-50 cursor-pointer">
                      <i className="ri-heart-fill text-red-500 text-lg"></i>
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-1 cursor-pointer hover:text-[#6366F1]" onClick={() => navigate(`/listing/${fav.listing_id}`)}>{fav.listing?.title || 'Space'}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                      <i className="ri-map-pin-line"></i>
                      <span>{fav.listing?.suburb && fav.listing?.state ? `${fav.listing.suburb}, ${fav.listing.state}` : 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-bold text-[#6366F1]">${fav.listing?.price_per_month || 0}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                      <button onClick={() => navigate(`/listing/${fav.listing_id}`)} className="px-4 py-2 bg-[#6366F1] text-white font-semibold rounded-lg hover:bg-[#4F46E5] cursor-pointer text-sm whitespace-nowrap">View</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-4xl text-red-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No favourites yet</h3>
              <p className="text-gray-600 mb-6">Browse spaces and tap the heart icon to save your favourites</p>
              <button onClick={() => navigate('/browse')} className="inline-flex items-center gap-2 px-6 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9C8E] cursor-pointer whitespace-nowrap">
                <i className="ri-search-line"></i>Browse Spaces
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}